import React from "react";
import {ForeignUser} from "../../../Redux/Types";
import {GetProfileTracksResponse} from "../../../API/Models";
import MusicList, {Entry} from "../MusicList/MusicList";
import {getProfileTracks} from "../../../API/Calls";
import Skeleton from "./Skeleton";
import {convertDateRange, Ranges} from "../ProfileDateRange/ProfileDateRange";
import styles from "./UserHistory.module.scss";
import {titleManager} from "../../../Title";
import DefaultAlbum from "../../../Images/DefaultAlbum.svg";
import {spotifyUriToId} from "../../../SpotifyUtil";
import SongSubject from "../MusicSubject/SongSubject";
import {OPEN_IN_SPOTIFY} from "../MusicActionPopup/MusicAction";

interface TopSongsProps {
    user: ForeignUser;
    page: number;
    pageFunc: (hasNext: boolean, hasPrev: boolean, pageNumber: number, totalPages: number, totalResults: number) => void,
    searchFilter?: string;
    dateRange: Ranges;
}

interface TopSongsState {
    /**
     * Whether the tracks have loaded
     */
    tracksLoaded: boolean;
    /**
     * Response with track history info
     */
    tracksResponse: GetProfileTracksResponse | null;
    signal: AbortController;
}

export default class TopSongs extends React.Component<TopSongsProps, TopSongsState> {

    constructor(props: Readonly<TopSongsProps>) {
        super(props);

        this.state = {
            tracksLoaded: false,
            tracksResponse: null,
            signal: new AbortController()
        }
    }

    render() {
        return (
            <div className={"TopSongsPage"}>
                {this.renderMusicList()}
            </div>
        )
    }

    renderMusicList() {
        if (!this.state.tracksLoaded || !this.state.tracksResponse) {
            return <Skeleton/>
        }
        if (this.state.tracksResponse.tracks.length === 0) {
            if (this.props.dateRange !== Ranges.AllTime || this.props.searchFilter) {
                return <div className={styles.Placeholder}>No results found</div>
            } else {
                return <div className={styles.Placeholder}>This user has no listening history</div>
            }
        }
        return (
            <MusicList
                entries={this.getTrackHistoryAsEntries()}
                metricType={"count"}
                defaultSize={50}
                className={"RecentHistoryList"}
                autoCollapse={true}
                removeLastRowBorder={true}
                isMobile={window.innerWidth <= 768}
                actionGenerator={entry => [
                    OPEN_IN_SPOTIFY(entry.playerLink)
                ]}
            />
        )
    }

    getTracksPromise(): Promise<any> {
        if (this.props.user && this.props.user.id) {
            return getProfileTracks(
                this.props.user.id, 50, this.props.page - 1, convertDateRange(this.props.dateRange), 0, this.props.searchFilter, this.state.signal)
                .then(tracks => {
                    this.setState({
                        tracksLoaded: true,
                        tracksResponse: tracks,
                    }, () => {
                        this.props.pageFunc(
                            this.state.tracksLoaded && !!this.state.tracksResponse && this.state.tracksResponse.pages > this.props.page,
                            this.state.tracksLoaded && this.props.page > 1,
                            this.state.tracksLoaded ? this.props.page : 0,
                            this.state.tracksResponse?.pages ?? 0,
                            this.state.tracksResponse?.total_tracks ?? 0,
                        )
                    })
                })
        } else {
            return Promise.reject("No profile loaded.");
        }
    }

    loadPage(): void {
        this.abortAndReset(() => {
            window.scrollTo(0, 0);
            this.props.pageFunc(false, false, 0, 0, 0);
            this.setState({tracksLoaded: false});
            this.getTracksPromise()
                .catch(err => console.error(err));
        });
    }

    componentDidMount(): void {
        titleManager.set(`${this.props.user.username}'s Top Songs`);
        this.loadPage();
    }

    componentDidUpdate(prevProps: Readonly<TopSongsProps>, prevState: Readonly<TopSongsState>, snapshot?: any): void {
        if (prevProps.searchFilter !== this.props.searchFilter) {
            this.loadPage();
            return;
        }
        if (prevProps.page !== this.props.page) {
            this.loadPage();
            return;
        }
        if (prevProps.dateRange !== this.props.dateRange) {
            this.loadPage();
            return;
        }
    }

    componentWillUnmount(): void {
        this.state.signal.abort();
    }

    abortAndReset(cb?: () => void) {
        this.state.signal.abort();
        this.setState({signal: new AbortController()}, cb);
    }

    private getTrackHistoryAsEntries(): Entry[] {
        if (this.state.tracksResponse) {
            return this.state.tracksResponse.tracks.map(track => {
                // Spotify URL hack
                const url = `https://open.spotify.com/track/${spotifyUriToId(track.track.uri)}`;
                const image = track.track.album.images
                    .sort((a, b) => a.width - b.width)[0]?.url ?? DefaultAlbum;
                return {
                    id: track.track.uri,
                    primary: track.track.name,
                    secondary: track.track.artists[0].name,
                    metric: track.count.toLocaleString(),
                    icon: image,
                    playerLink: url,
                    actionSubject: () => <SongSubject name={track.track.name} artist={track.track.artists[0].name}
                                                      image={image}/>
                }
            });
        } else {
            return [];
        }
    }
}
